import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Blog.css';

const PER_PAGE   = 5;
const MAX_DEPTH  = 3;  // máximo 3 níveis de resposta

// ── Utilitários ────────────────────────────────────────────
function detectVideo(content, type) {
  if (type !== 'video') return null;
  const yt = content?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vi = content?.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vi) return `https://player.vimeo.com/video/${vi[1]}`;
  return null;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'Agora';
  if (m < 60) return `${m}min atrás`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h atrás`;
  return `${Math.floor(h / 24)}d atrás`;
}

async function bFetch(path, opts = {}) {
  const res = await fetch(path, { credentials: 'include', ...opts });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Decodifica entidades HTML para textos (ex: &quot; → ", &amp; → &)
// Necessário para títulos salvos com express-validator .escape()
function decodeHtml(str) {
  if (!str) return '';
  const el = document.createElement('div');
  el.innerHTML = str;
  return el.textContent || el.innerText || str;
}

function buildTree(flat) {
  const map = {};
  const roots = [];
  flat.forEach(c => { map[c.id] = { ...c, replies: [] }; });
  flat.forEach(c => {
    if (c.parent_comment_id && map[c.parent_comment_id]) {
      map[c.parent_comment_id].replies.push(map[c.id]);
    } else {
      roots.push(map[c.id]);
    }
  });
  return roots;
}

// ── CommentNode — fora do Blog para evitar remount ─────────
function CommentNode({
  comment, depth,
  user, replyTo, replyTexts, replySubmitting, expandedReplies,
  onToggleReply, onSubmitReply, onReplyTextChange, onToggleExpand,
}) {
  const isReplying = replyTo === comment.id;
  const hasReplies = comment.replies?.length > 0;
  const isExpanded = !!expandedReplies[comment.id];
  const indent     = depth * 20;
  const replyCount = comment.replies?.length || 0;

  return (
    <div style={{
      marginLeft: indent,
      borderLeft: depth > 0 ? '2px solid var(--border-md)' : 'none',
      paddingLeft: depth > 0 ? 12 : 0,
      marginTop: 10,
    }}>
      {/* Comentário */}
      <div className="blog-comment">
        <div className="blog-comment-avatar" style={{
          background: depth === 0 ? 'linear-gradient(135deg,#1e90ff,#16c2fb)' : '#e2e8f0',
          color: depth === 0 ? '#fff' : '#64748b',
        }}>
          {comment.username?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="blog-comment-body">
          <div style={{ display:'flex', alignItems:'baseline', gap:8, flexWrap:'wrap' }}>
            <span className="blog-comment-author">{comment.username}</span>
            <span className="blog-comment-date">{timeAgo(comment.created_at)}</span>
          </div>
          <p className="blog-comment-text">{comment.content}</p>

          <div style={{ display:'flex', gap:12, marginTop:4, flexWrap:'wrap', alignItems:'center' }}>
            {/* Responder (só até profundidade 2, para não ultrapassar 3 níveis) */}
            {user && depth < MAX_DEPTH - 1 && (
              <button
                onClick={() => onToggleReply(comment.id)}
                style={{ background:'none', border:'none', color: isReplying ? '#e53935' : '#1e90ff', fontSize:'0.8rem', cursor:'pointer', fontWeight:600, padding:0, fontFamily:'inherit' }}
              >
                <i className={`fas fa-${isReplying ? 'times' : 'reply'}`} style={{ marginRight:4 }} />
                {isReplying ? 'Cancelar' : 'Responder'}
              </button>
            )}

            {/* Ver / ocultar respostas */}
            {hasReplies && (
              <button
                onClick={() => onToggleExpand(comment.id)}
                style={{ background:'none', border:'none', color:'#64748b', fontSize:'0.78rem', cursor:'pointer', fontWeight:600, padding:0, fontFamily:'inherit', display:'flex', alignItems:'center', gap:4 }}
              >
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
                {isExpanded
                  ? 'Ocultar respostas'
                  : `Ver ${replyCount} ${replyCount === 1 ? 'resposta' : 'respostas'}`}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Formulário de resposta */}
      {isReplying && (
        <form
          onSubmit={e => onSubmitReply(e, comment.id)}
          className="blog-comment-form"
          style={{ marginLeft:20, marginTop:8, marginBottom:4 }}
        >
          <div style={{ fontSize:'0.78rem', color:'#90a4ae', marginBottom:4 }}>
            <i className="fas fa-reply" style={{ marginRight:4 }} />
            Respondendo a <strong style={{ color:'#475569' }}>{comment.username}</strong>
          </div>
          <textarea
            placeholder="Escreva sua resposta..."
            value={replyTexts[comment.id] || ''}
            onChange={e => onReplyTextChange(comment.id, e.target.value)}
            required
            autoFocus
          />
          <button type="submit" className="blog-comment-submit" disabled={replySubmitting[comment.id]}>
            <i className="fas fa-paper-plane" />
            {replySubmitting[comment.id] ? 'Enviando...' : 'Responder'}
          </button>
        </form>
      )}

      {/* Respostas (colapsáveis, máx. 3 níveis) */}
      {hasReplies && isExpanded && depth < MAX_DEPTH - 1 && (
        <div>
          {comment.replies.map(reply => (
            <CommentNode
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              user={user}
              replyTo={replyTo}
              replyTexts={replyTexts}
              replySubmitting={replySubmitting}
              expandedReplies={expandedReplies}
              onToggleReply={onToggleReply}
              onSubmitReply={onSubmitReply}
              onReplyTextChange={onReplyTextChange}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Engagement — fora do Blog ──────────────────────────────
function Engagement({ post, onLike, onShare, onFavorite, isFavorite }) {
  return (
    <>
      <div className="blog-engagement">
        <button
          className={`blog-like-btn${post.liked ? ' liked' : ''}`}
          onClick={() => onLike(post.id)}
        >
          <i className={`fa${post.liked ? 's' : 'r'} fa-heart`} />
          {post.likes || 0}
        </button>
        <span className="blog-views"><i className="fas fa-eye" /> {post.views || 0}</span>
        {post.post_type === 'video' && post.video_duration && (
          <span className="blog-views"><i className="fas fa-clock" /> {post.video_duration}</span>
        )}
        {/* Bookmark — classe CSS controla a cor, sem inline style */}
        <button
          onClick={() => onFavorite(post.id)}
          className={`blog-bookmark-btn${isFavorite ? ' favorited' : ''}`}
          title={isFavorite ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
        >
          <i className="fas fa-bookmark" />
        </button>
      </div>
      <div className="blog-share">
        {[
          { k:'facebook',  icon:'fab fa-facebook-f',  cls:'fb', label:'Facebook'    },
          { k:'twitter',   icon:'fab fa-twitter',     cls:'tw', label:'Twitter'     },
          { k:'whatsapp',  icon:'fab fa-whatsapp',    cls:'wa', label:'WhatsApp'    },
          { k:'instagram', icon:'fab fa-instagram',   cls:'ig', label:'Instagram'   },
          { k:'copy',      icon:'fas fa-link',        cls:'cp', label:'Copiar link' },
        ].map(s => (
          <button key={s.k} className={`blog-share-btn ${s.cls}`} title={s.label}
            onClick={() => onShare(s.k, post.id)}>
            <i className={s.icon} />
          </button>
        ))}
      </div>
    </>
  );
}

// ── Blog (componente principal) ────────────────────────────
export default function Blog() {
  const { user, favoriteIds, toggleFavorite } = useAuth();
  const location = useLocation();

  const [categories,      setCategories]      = useState([]);
  const [posts,           setPosts]           = useState([]);
  const [totalPosts,      setTotalPosts]      = useState(0);
  const [page,            setPage]            = useState(1);
  const [selectedCat,     setSelectedCat]     = useState(null);
  const [selectedPost,    setSelectedPost]    = useState(null);
  const [comments,        setComments]        = useState([]);
  const [relatedPosts,    setRelatedPosts]    = useState([]);
  const [sidebarOpen,     setSidebarOpen]     = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [notif,           setNotif]           = useState(null);
  const [commentText,     setCommentText]     = useState('');
  const [submitting,      setSubmitting]      = useState(false);
  const [replyTo,         setReplyTo]         = useState(null);
  const [replyTexts,      setReplyTexts]      = useState({});
  const [replySubmitting, setReplySubmitting] = useState({});
  const [expandedReplies, setExpandedReplies] = useState({});
  const [commentsVisible, setCommentsVisible] = useState(true);
  const notifTimer = useRef(null);

  function showNotif(msg, type = 'info') {
    setNotif({ msg, type });
    clearTimeout(notifTimer.current);
    notifTimer.current = setTimeout(() => setNotif(null), 3000);
  }

  // Categorias
  useEffect(() => {
    bFetch('/posts/categories/public').then(setCategories).catch(() => {});
  }, []);

  // Posts
  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const url = selectedCat ? `/posts/category/${selectedCat}` : '/posts/public';
      const data = await bFetch(url);
      setTotalPosts(data.length);
      const slice = data.slice((page - 1) * PER_PAGE, page * PER_PAGE);
      setPosts(slice);
      slice.forEach(p => fetch(`/posts/view/${p.id}`, { method:'POST', credentials:'include' }).catch(() => {}));
    } catch {
      showNotif('Erro ao carregar posts.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedCat, page]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  // Abrir post
  async function openPost(postId) {
    setLoading(true);
    setExpandedReplies({});
    setReplyTo(null);
    setReplyTexts({});
    setCommentsVisible(true);
    try {
      const post = await bFetch(`/posts/post/${postId}`);
      setSelectedPost(post);
      fetch(`/posts/view/${postId}`, { method:'POST', credentials:'include' }).catch(() => {});
      loadComments(postId);
      if (post.category_id) {
        const rel = await bFetch(`/posts/category/${post.category_id}`);
        setRelatedPosts(rel.filter(p => p.id !== postId).slice(0, 3));
      }
      window.scrollTo({ top:0, behavior:'smooth' });
    } catch {
      showNotif('Erro ao abrir post.', 'error');
    } finally {
      setLoading(false);
    }
  }

  // Abre post direto quando vindo do Perfil (favoritos)
  useEffect(() => {
    const id = location.state?.openPostId;
    if (id) {
      openPost(id);
      // Limpa o estado da rota para não reabrir ao atualizar a página
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function backToList() {
    setSelectedPost(null);
    setComments([]);
    setRelatedPosts([]);
    setReplyTo(null);
    setReplyTexts({});
    setExpandedReplies({});
  }

  // Comentários
  async function loadComments(postId) {
    try {
      const data = await bFetch(`/posts/comments/${postId}`);
      setComments(data);
    } catch {}
  }

  // Submeter comentário
  async function submitComment(e, parentId = null) {
    e.preventDefault();
    if (!user) { showNotif('Faça login para comentar.', 'error'); return; }
    const content = parentId ? (replyTexts[parentId] || '') : commentText;
    if (!content.trim()) return;

    if (parentId) setReplySubmitting(p => ({ ...p, [parentId]: true }));
    else          setSubmitting(true);

    try {
      const res = await fetch(`/posts/comment/${selectedPost.id}`, {
        method:'POST', headers:{ 'Content-Type':'application/json' }, credentials:'include',
        body: JSON.stringify({ content: content.trim(), parent_comment_id: parentId || null }),
      });
      if (res.status === 401) { showNotif('Faça login para comentar.', 'error'); return; }
      if (!res.ok) throw new Error();

      if (parentId) {
        setReplyTexts(p => ({ ...p, [parentId]: '' }));
        setReplyTo(null);
        // Expande automaticamente as respostas do pai após enviar
        setExpandedReplies(p => ({ ...p, [parentId]: true }));
      } else {
        setCommentText('');
      }
      loadComments(selectedPost.id);
      showNotif('Comentário enviado!', 'success');
    } catch {
      showNotif('Erro ao enviar comentário.', 'error');
    } finally {
      if (parentId) setReplySubmitting(p => ({ ...p, [parentId]: false }));
      else          setSubmitting(false);
    }
  }

  // Toggle reply form
  function handleToggleReply(commentId) {
    setReplyTo(prev => (prev === commentId ? null : commentId));
  }

  // Atualizar texto de resposta
  function handleReplyTextChange(commentId, value) {
    setReplyTexts(p => ({ ...p, [commentId]: value }));
  }

  // Expandir / colapsar respostas de um comentário
  function handleToggleExpand(commentId) {
    setExpandedReplies(p => ({ ...p, [commentId]: !p[commentId] }));
  }

  // Favoritar — delega ao AuthContext (estado global + API)
  async function handleFavorite(postId) {
    if (!user) { showNotif('Faça login para favoritar.', 'error'); return; }
    try {
      const isFav = await toggleFavorite(postId);
      showNotif(isFav ? '⭐ Salvo nos favoritos!' : 'Removido dos favoritos.', 'success');
    } catch {
      showNotif('Erro ao favoritar.', 'error');
    }
  }

  // Like
  async function handleLike(postId) {
    if (!user) { showNotif('Faça login para curtir.', 'error'); return; }
    try {
      const data = await bFetch(`/posts/like/${postId}`, {
        method:'POST', headers:{ 'Content-Type':'application/json' },
      });
      const upd = p => p.id === postId ? { ...p, likes: data.likes, liked: data.liked } : p;
      setPosts(prev => prev.map(upd));
      if (selectedPost?.id === postId) setSelectedPost(prev => ({ ...prev, likes: data.likes, liked: data.liked }));
    } catch {
      showNotif('Erro ao curtir.', 'error');
    }
  }

  // Compartilhar
  function sharePost(platform, postId) {
    fetch(`/posts/share/${postId}`, {
      method:'POST', headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify({ platform }), credentials:'include',
    }).catch(() => {});
    const url = encodeURIComponent(`${window.location.origin}/blog/post/${postId}`);
    const map = {
      facebook:  `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter:   `https://twitter.com/intent/tweet?url=${url}`,
      whatsapp:  `https://api.whatsapp.com/send?text=${url}`,
    };
    if (platform === 'copy') {
      navigator.clipboard.writeText(decodeURIComponent(url))
        .then(() => showNotif('Link copiado!', 'success'));
    } else if (platform === 'instagram') {
      const postUrl = decodeURIComponent(url);
      if (navigator.share) {
        navigator.share({ url: postUrl }).catch(() => {});
      } else {
        navigator.clipboard.writeText(postUrl).then(() => {
          showNotif('Link copiado! Abra o Instagram e cole no Stories ou na bio.', 'success');
          setTimeout(() => window.open('https://www.instagram.com', '_blank', 'noopener'), 800);
        });
      }
    } else {
      window.open(map[platform], '_blank', 'noopener');
    }
  }

  const totalPages = Math.ceil(totalPosts / PER_PAGE);

  const commentNodeProps = {
    user, replyTo, replyTexts, replySubmitting, expandedReplies,
    onToggleReply: handleToggleReply,
    onSubmitReply: submitComment,
    onReplyTextChange: handleReplyTextChange,
    onToggleExpand: handleToggleExpand,
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="blog-page">

      <div className={`blog-sidebar-overlay${sidebarOpen ? ' open' : ''}`}
           onClick={() => setSidebarOpen(false)} />

      <aside className={`blog-sidebar${sidebarOpen ? ' open' : ''}`}>
        <h3><i className="fas fa-th-list" style={{ marginRight:8 }} />Categorias</h3>
        <ul className="blog-cat-list">
          <li className={`blog-cat-item${!selectedCat ? ' active' : ''}`}
              onClick={() => { setSelectedCat(null); setPage(1); setSidebarOpen(false); }}>
            <i className="fas fa-globe" /> Todos
          </li>
          {categories.map(c => (
            <li key={c.id}
                className={`blog-cat-item${selectedCat === c.id ? ' active' : ''}`}
                onClick={() => { setSelectedCat(c.id); setPage(1); setSidebarOpen(false); }}>
              <i className="fas fa-tag" /> {c.name}
            </li>
          ))}
        </ul>
      </aside>

      <button className="blog-sidebar-btn" onClick={() => setSidebarOpen(o => !o)}>
        <i className="fas fa-bars" />
      </button>

      <main className="blog-main">

        {selectedPost ? (
          /* ─── Detalhe do post ─── */
          <div className="blog-post-detail">
            <button className="blog-back-btn" onClick={backToList}>
              <i className="fas fa-arrow-left" /> Voltar ao blog
            </button>

            <h1>{decodeHtml(selectedPost.title)}</h1>

            <div className="blog-article-meta">
              {selectedPost.category_name && (
                <span><i className="fas fa-folder" /> {selectedPost.category_name}</span>
              )}
              <span>
                <i className="fas fa-calendar" />{' '}
                {new Date(selectedPost.created_at).toLocaleDateString('pt-BR')}
              </span>
              {selectedPost.post_type === 'video' && (
                <span style={{ background:'#e53935', color:'#fff', borderRadius:4, padding:'2px 8px', fontSize:'0.78rem' }}>
                  <i className="fas fa-play" /> Vídeo
                </span>
              )}
            </div>

            {detectVideo(selectedPost.content, selectedPost.post_type) ? (
              <div className="blog-video-wrap">
                <iframe src={detectVideo(selectedPost.content, selectedPost.post_type)}
                        title={selectedPost.title} allowFullScreen />
              </div>
            ) : (
              <div className="blog-content"
                   dangerouslySetInnerHTML={{ __html: selectedPost.content }} />
            )}

            <Engagement
              post={selectedPost}
              onLike={handleLike}
              onShare={sharePost}
              onFavorite={handleFavorite}
              isFavorite={favoriteIds.has(selectedPost.id)}
            />

            {relatedPosts.length > 0 && (
              <div className="blog-related">
                <h4><i className="fas fa-link" style={{ marginRight:6 }} />Veja também</h4>
                <ul>
                  {relatedPosts.map(p => (
                    <li key={p.id}>
                      <a href="#!" onClick={e => { e.preventDefault(); openPost(p.id); }}>
                        {p.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ─── Comentários ─── */}
            <div className="blog-comments">
              {/* Título clicável para mostrar/ocultar */}
              <button
                onClick={() => setCommentsVisible(v => !v)}
                style={{ background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', width:'100%', textAlign:'left', padding:'12px 0', display:'flex', alignItems:'center', gap:8, borderBottom:'1px solid var(--border-md)', marginBottom:12 }}
              >
                <i className="fas fa-comments" style={{ color:'#1e90ff', fontSize:'1rem' }} />
                <span style={{ fontWeight:700, fontSize:'1rem', color:'var(--text-primary)', flex:1 }}>
                  Comentários ({comments.length})
                </span>
                <i className={`fas fa-chevron-${commentsVisible ? 'up' : 'down'}`} style={{ color:'#90a4ae', fontSize:'0.85rem' }} />
              </button>

              {commentsVisible && (
                <>
                  {comments.length === 0 ? (
                    <p style={{ color:'#90a4ae', fontSize:'0.9rem', padding:'8px 0 16px' }}>
                      Nenhum comentário ainda. Seja o primeiro!
                    </p>
                  ) : (
                    buildTree(comments).map(c => (
                      <CommentNode key={c.id} comment={c} depth={0} {...commentNodeProps} />
                    ))
                  )}

                  {/* Formulário de comentário principal */}
                  <form className="blog-comment-form" onSubmit={e => submitComment(e, null)} style={{ marginTop:20 }}>
                    <textarea
                      placeholder={user ? 'Adicione um comentário...' : 'Faça login para comentar'}
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      disabled={!user}
                      required
                    />
                    <button type="submit" className="blog-comment-submit" disabled={submitting || !user}>
                      <i className="fas fa-paper-plane" />
                      {submitting ? 'Enviando...' : 'Comentar'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>

        ) : (
          /* ─── Lista de posts ─── */
          <>
            <h1>
              <i className="fas fa-newspaper" />{' '}Artigos Recentes
              {selectedCat && (
                <span style={{ fontSize:'1rem', color:'#90a4ae', fontWeight:400 }}>
                  {' — '}{categories.find(c => c.id === selectedCat)?.name}
                </span>
              )}
            </h1>

            {loading && (
              <div style={{ textAlign:'center', padding:'40px 0', color:'#90a4ae' }}>
                <i className="fas fa-spinner fa-spin fa-2x" />
              </div>
            )}

            {!loading && posts.map(post => {
              const embedUrl = detectVideo(post.content, post.post_type);
              return (
                <div key={post.id} className="blog-article">
                  <h2 onClick={() => openPost(post.id)}>
                    <span className="blog-title-text">{decodeHtml(post.title)}</span>
                  </h2>

                  <div className="blog-article-meta">
                    {post.category_name && (
                      <span><i className="fas fa-folder" /> {post.category_name}</span>
                    )}
                    <span>
                      <i className="fas fa-calendar" />{' '}
                      {new Date(post.created_at).toLocaleDateString('pt-BR')}
                    </span>
                    {post.post_type === 'video' && (
                      <span style={{ background:'#e53935', color:'#fff', borderRadius:4, padding:'2px 6px', fontSize:'0.75rem' }}>
                        <i className="fas fa-play" /> Vídeo
                      </span>
                    )}
                  </div>

                  {embedUrl ? (
                    <div className="blog-video-wrap">
                      <iframe src={embedUrl} title={post.title} allowFullScreen />
                    </div>
                  ) : (
                    <div className="blog-content"
                         style={{ maxHeight:120, overflow:'hidden', maskImage:'linear-gradient(to bottom, black 60%, transparent)' }}
                         dangerouslySetInnerHTML={{ __html: post.content }} />
                  )}

                  <button onClick={() => openPost(post.id)}
                          style={{ background:'none', border:'none', color:'#1e90ff', fontWeight:600, fontSize:'0.88rem', cursor:'pointer', marginBottom:12, fontFamily:'inherit', padding:0 }}>
                    Ler mais <i className="fas fa-arrow-right" />
                  </button>

                  <Engagement
                    post={post}
                    onLike={handleLike}
                    onShare={sharePost}
                    onFavorite={handleFavorite}
                    isFavorite={favoriteIds.has(post.id)}
                  />
                </div>
              );
            })}

            {!loading && posts.length === 0 && (
              <p style={{ color:'#90a4ae', textAlign:'center', padding:'40px 0' }}>
                Nenhum post encontrado.
              </p>
            )}

            {totalPages > 1 && (
              <div className="blog-pagination">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button key={n}
                          className={`blog-page-btn${n === page ? ' active' : ''}`}
                          onClick={() => { setPage(n); window.scrollTo({ top:0, behavior:'smooth' }); }}>
                    {n}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {notif && <div className={`blog-notif ${notif.type}`}>{notif.msg}</div>}
    </div>
  );
}
